import { useState, useEffect } from 'react';
import { useDashboardData } from '../useDashboardData';

export const useTodayData = (selectedLocation) => {
    const { eventData } = useDashboardData();
    const [todayData, setTodayData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [error, setError] = useState('');
    const [fetchLoading, setFetchLoading] = useState(false);

    // 今日の日付を取得（YYYY-MM-DD形式）
    const getTodaysDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // 場所名をAPIで使用する形式に変換
    const getApiLocation = (location) => {
        if (!location) return '';
        const locationMap = {
            'omotesando': 'omotesando',
            'yottekan': 'yottekan',
            'honmachi4': 'honmachi4',
            'honmachi3': 'honmachi3',
            'honmachi2': 'honmachi2',
            'kokubunjidori': 'kokubunjidori',
            'yasukawadori': 'yasukawadori',
            'jinnya': 'jinnya',
            'nakabashi': 'nakabashi',
            'old-town': 'old-town',
            'station': 'station',
            'gyouzinbashi': 'gyouzinbashi'
        };
        return locationMap[location] || location;
    };

    // 指定した日付のイベント情報を取得
    const getEventsForDate = (dateStr) => {
        if (!eventData || !Array.isArray(eventData)) return [];
        
        try {
            return eventData.filter(event => event.date === dateStr) || [];
        } catch (error) {
            console.error('Error in getEventsForDate:', error, 'dateStr:', dateStr);
            return [];
        }
    };

    useEffect(() => {
        const fetchTodayData = async () => {
            if (!selectedLocation) return;

            setFetchLoading(true);
            setError('');
            
            try {
                const apiLocation = getApiLocation(selectedLocation);
                const todaysDate = getTodaysDate();
                const weeksCount = 3; // 3週間分のデータを取得
                
                const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/';
                
                const fetchWithTimeout = (url, options = {}, timeout = 30000) => {
                    return Promise.race([
                        fetch(url, options),
                        new Promise((_, reject) => 
                            setTimeout(() => reject(new Error('Request timeout')), timeout)
                        )
                    ]);
                };

                // 詳細データを取得（weeks_countパラメータ追加）
                const detailResponse = await fetchWithTimeout(
                    `${baseUrl}congestion-data/${apiLocation}?target_date=${todaysDate}&weeks_count=${weeksCount}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                if (!detailResponse.ok) {
                    throw new Error(`詳細データの取得に失敗しました (${detailResponse.status})`);
                }
                
                const detailData = await detailResponse.json();
                setTodayData(detailData);

                // サマリーデータを取得（weeks_countパラメータ追加）
                const summaryResponse = await fetchWithTimeout(
                    `${baseUrl}congestion-data/${apiLocation}/summary?target_date=${todaysDate}&weeks_count=${weeksCount}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );
                
                if (!summaryResponse.ok) {
                    throw new Error(`サマリーデータの取得に失敗しました (${summaryResponse.status})`);
                }
                
                const summaryDataResult = await summaryResponse.json();
                
                // サマリーデータに詳細データの一部をマージして補完
                if (summaryDataResult.data && detailData.data) {
                    // recent_week_daily_summaryが期待より少ない場合、詳細データから補完
                    const expectedDays = weeksCount * 7;
                    const currentSummaryLength = summaryDataResult.data.recent_week_daily_summary?.length || 0;
                    
                    if (currentSummaryLength < expectedDays) {
                        // 詳細データのextended_weekから補完
                        const extendedWeekData = detailData.data.extended_week?.daily_data || [];
                        
                        if (extendedWeekData.length > currentSummaryLength) {
                            const supplementedData = extendedWeekData.map(day => ({
                                date: day.date,
                                day_of_week: day.day_of_week,
                                congestion_level: day.congestion_level,
                                is_weekend: day.is_weekend,
                                week_of_month_label: day.week_of_month_label,
                                is_today: day.is_today || false,
                                days_from_today: day.days_from_today || 0,
                                weather_info: day.weather_info || null  // 天気情報を追加
                            }));
                            
                            summaryDataResult.data.recent_week_daily_summary = supplementedData;
                            summaryDataResult.data.actual_days = supplementedData.length;
                        }
                    }
                    
                    // 詳細データも追加してフォールバック用に保持
                    summaryDataResult.data.extended_week = detailData.data.extended_week;
                    summaryDataResult.data.historical_comparison = detailData.data.historical_comparison;
                }
                
                setSummaryData(summaryDataResult);
                
            } catch (err) {
                console.error('Error fetching today data:', err);
                if (err.message.includes('timeout')) {
                    setError('通信がタイムアウトしました。しばらく待ってから再度お試しください。');
                } else if (err.message.includes('Failed to fetch')) {
                    setError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
                } else {
                    setError(`データの取得中にエラーが発生しました: ${err.message}`);
                }
            } finally {
                setFetchLoading(false);
            }
        };

        fetchTodayData();
    }, [selectedLocation]);

    return {
        todayData,
        summaryData,
        error,
        fetchLoading,
        getTodaysDate,
        getEventsForDate
    };
};
